/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';

import styles from './IntroScreen.style';

import OnboardingIntroBackground from './OnboardingIntroBackground';
import messages from 'src/shared/app/onboarding/l10n/onboardingL10n';
import Markdown from 'src/shared/app/base/component/text/Markdown';
import VirtualBraindateSupportModal from 'plugin/virtual-braindate/component/modal/VirtualBraindateSupportModal';
import EventBranding from 'src/shared/app/nav/component/branding/event/EventBranding';
import PrimaryButton from 'src/shared/ui/component/button/PrimaryButton';
import HeroText from 'src/shared/ui/component/text/HeroText';
import Footer from '../footer/Footer';

type Props = {
  eventName: string,
  customTitle: string,
  customSubtitle: string,
  customCTA: string,
  handleClick: () => void,
  isVideoCallEnabled: boolean,
  isVideoCallSupported: boolean,
};

const useStyles = createUseStyles(styles);

const IntroScreenDumb = ({
  eventName,
  customTitle,
  customSubtitle,
  customCTA,
  handleClick,
  isVideoCallEnabled,
  isVideoCallSupported,
}: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | State
   |----------------------------------------------------------------------------
   */

  const [isSupportModalShown, setSupportModalShown] = React.useState(false);

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const intl = useIntl();

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onSupportModalClose = () => handleClick();

  const onClick = () => {
    if (isVideoCallEnabled && !isVideoCallSupported) {
      setSupportModalShown(true);
    } else {
      handleClick();
    }
  };

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const supportModal = isSupportModalShown && (
    <VirtualBraindateSupportModal handleClose={onSupportModalClose} />
  );

  const title =
    customTitle ||
    (isVideoCallEnabled
      ? intl.formatMessage(messages.titleVideoCall, { eventName })
      : intl.formatMessage(messages.titleGeneric, { eventName }));

  const subtitle =
    customSubtitle ||
    (isVideoCallEnabled
      ? intl.formatMessage(messages.subtitleVideoCall, { eventName })
      : intl.formatMessage(messages.subtitleGeneric, { eventName }));

  const cta =
    customCTA ||
    (isVideoCallEnabled
      ? intl.formatMessage(messages.ctaVideoCall, { eventName })
      : intl.formatMessage(messages.ctaGeneric, { eventName }));

  return (
    <div>
      {supportModal}
      <div className={classes.root}>
        <OnboardingIntroBackground />
        <div className={classes.branding}>
          <EventBranding />
        </div>
        <div className={classes.body}>
          <div>
            <HeroText as="h1" layoutClass={classes.hero}>
              <Markdown>{title}</Markdown>
            </HeroText>
            <HeroText small>
              <Markdown>{subtitle}</Markdown>
            </HeroText>
            <div className={classes.submit}>
              <PrimaryButton handleClick={onClick}>{cta}</PrimaryButton>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default compose(React.memo)(IntroScreenDumb);
